<template>
  <div v-if="auth.isFullyLoggedIn">
    <h1 class="text-xl font-medium font-display">
      {{ $t("sso-profile.title") }}
    </h1>
    <hr class="my-4" />
    <template v-if="$tenant.supportsSsoProfilePage()">
      <div v-if="ssoUser == 'loading'">
        <Spinner/>
      </div>
      <div v-else-if="!ssoUser.email">
        {{ssoUser}}
      </div>
      <div v-else>
        <h4 class="text-l font-medium font-display">{{ $t('sso-profile.update-password.subtitle') }}</h4>
        <p class="hint mb-2">{{ $t('sso-profile.update-password.hint') }}</p>
        <ButtonWithLoadingIndicator
          class="submit btn btn-primary mb-2"
          type="submit"
          :disabled="submittingExecuteAction"
          :loading="submittingExecuteAction"
          @click="executeAction"
        >
          {{ $t("sso-profile.update-password.button") }}
        </ButtonWithLoadingIndicator>
        <p style="height: 24px;">{{resultExecuteAction}}</p>

        <template v-if="$tenant.supportsSsoProfilePageEditEmail()">
          <hr class="my-4" />

          <h4 class="text-l font-medium font-display">{{ $t('sso-profile.update-user-form.subtitle') }}</h4>
          <p class="hint mb-2">{{ $t('sso-profile.update-user-form.hint') }}</p>
          <form @submit.prevent="updateUser">
            <FormInput
              type="email"
              class="w-full"
              :label="$t('sso-profile.update-user-form.email.label')"
              max-length="50"
              v-model.trim="ssoUser.email"
              required
            />
            <ButtonBar class="mt-6">
              <ButtonWithLoadingIndicator
                class="submit btn btn-primary mb-2"
                type="submit"
                :disabled="submittingUpdateUser"
                :loading="submittingUpdateUser"
              >
                {{ $t('sso-profile.update-user-form.button') }}
              </ButtonWithLoadingIndicator>
            </ButtonBar>
          </form>
          <p style="height: 48px;">{{resultUpdateUser}}</p>
        </template>

        <template v-if="$tenant.supportsSsoProfilePageDeleteEmail()">
          <hr class="my-4" />

          <h4 class="text-l font-medium font-display">{{ $t('sso-profile.delete-sso-user.subtitle') }}</h4>
          <p class="hint mb-2">{{ $t('sso-profile.delete-sso-user.hint') }}</p>
          <ButtonBar class="mt-6">
            <button class="btn btn-primary" @click="showConfirmDeleteAlert()">
              {{ $t('sso-profile.delete-sso-user.button') }}
            </button>
          </ButtonBar>
          <AlertDialog
            name="confirm-delete-alert"
            form="delete-sso-user-form"
            :positive-text="$t('sso-profile.delete-sso-user.button')"
            :negative-text="$t('app.cancel')"
            @negative="hideConfirmDeleteAlert()"
            :loading="submittingDeleteUser"
            click-to-close
            disable-auto-hide
          >
            <form @submit.prevent="deleteUser" id="delete-sso-user-form">
              <p class="font-semibold">{{ $t('sso-profile.delete-sso-user.button') }}</p>
              <p>{{ t('sso-profile.delete-sso-user.confirm') }}</p>
              <FormInput
                type="email"
                v-model.trim="deleteConfirmEmail"
                class="w-full"
                :label="$t('sso-profile.delete-sso-user.email.label')"
                :disabled="submittingDeleteUser"
                max-length="50"
                required
              />
            </form>
            <p style="height: 24px;">{{resultDeleteUser}}</p>
          </AlertDialog>
        </template>
      </div>
    </template>
    <FeatureDisabledAlert v-else></FeatureDisabledAlert>
  </div>
  <div v-else>
    <p class="text-center">{{t('auth.logged-out')}}</p>
  </div>
</template>

<script lang="ts" setup>
import { useAuth } from "@/pinia/auth";
import { useTenant } from "@/plugins/tenant";
import { useAxios } from "@/plugins/axios";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useVfm } from "@/plugins/vfm";
import ButtonWithLoadingIndicator from "@/components/ButtonWithLoadingIndicator.vue";
import { ref } from "vue";
import FeatureDisabledAlert from "@/components/FeatureDisabledAlert.vue";
import FormInput from "@/components/form/FormInput.vue";
import ButtonBar from "@/components/ButtonBar.vue";
import FormCheckbox from "@/components/form/FormCheckbox.vue";
import AlertDialog from "@/components/AlertDialog.vue";
import Spinner from "@/components/Spinner.vue";

const auth = useAuth()
const tenant = useTenant()
const { t } = useI18n()
const route = useRoute()
const axios = useAxios()
const vfm = useVfm()

const ssoUser = ref();

const submittingExecuteAction = ref(false);
const resultExecuteAction = ref('');

const submittingUpdateUser = ref(false);
const resultUpdateUser = ref('');

const submittingDeleteUser = ref(false);
const resultDeleteUser = ref('');

async function loadSsoProfile() {
  ssoUser.value = 'loading';

  const response = await axios.get(
    `/sso-profile/getProfile`
  );

  ssoUser.value = response.data;
}
loadSsoProfile();


async function executeAction() {
   try {
    submittingExecuteAction.value = true;
    resultExecuteAction.value = '';

    const response = await axios.post(
      `/sso-profile/executeUpdatePassword`
    );

    // empty response means success
    if (response.data == '') {
      resultExecuteAction.value = t("sso-profile.update-password.success-message");
    } else {
      console.log(response.data);
      resultExecuteAction.value = ""+response.data;
    }
  } catch (error) {
    console.log(error);
    resultExecuteAction.value = 'error';
  } finally {
    submittingExecuteAction.value = false;
  }
}

async function updateUser() {
   try {
    submittingUpdateUser.value = true;
    resultUpdateUser.value = '';

    const response = await axios.post(
      `/sso-profile/updateUser`,
      {
        email: ssoUser.value.email
      }
    );

    // empty response means success
    if (response.data == '') {
      resultUpdateUser.value = t("sso-profile.update-user-form.success-message");
    } else {
      console.log(response.data);
      resultUpdateUser.value = ""+response.data;
    }
  } catch (error) {
    console.log(error);
    resultUpdateUser.value = 'error';
  } finally {
    submittingUpdateUser.value = false;
  }
}

function showConfirmDeleteAlert() {
  vfm.show('confirm-delete-alert');
  deleteConfirmEmail.value = '';
  resultDeleteUser.value = '';
}

function hideConfirmDeleteAlert() {
  vfm.hide('confirm-delete-alert');
}

const deleteConfirmEmail = ref('');

async function deleteUser() {
   try {
    submittingDeleteUser.value = true;
    resultDeleteUser.value = '';

    const response = await axios.post(
      `/sso-profile/deleteUser`,
      {
        email: deleteConfirmEmail.value
      }
    );

    // empty response means success
    if (response.data == '') {
      vfm.hide('confirm-delete-alert');

      // send postmessage to origin to notify that the account has been deleted
      if (window.parent) {
        window.parent.postMessage('accountDeleted', '*');
      }

      auth.logOut(false);
    } else {
      console.log(response.data);
      resultDeleteUser.value = ""+response.data;
    }
  } catch (error) {
    console.log(error);
    resultDeleteUser.value = 'error';
  } finally {
    submittingDeleteUser.value = false;
  }
}

</script>
